import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, ButtonGroup, Button,
  CircularProgress, Divider, Stack
} from '@mui/material';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  PieChart, Pie, Cell
} from 'recharts';

import { Insights, Schedule } from '@mui/icons-material';
import Calendar from '../Calendar';
import api from '../../utils/api';

const InsightsWithCalendar = () => {
  const [selectedWidget, setSelectedWidget] = useState('subtypes');
  const [insights, setInsights] = useState(null);
  const [subtypeMap, setSubtypeMap] = useState({});
  const [loading, setLoading] = useState(true);

  const fetchInsights = async () => {
    try {
      setLoading(true);
      const [insightsRes, subtypesRes] = await Promise.all([
        api.get('/workorder-insights'),
        api.get('/workorder-subtypes'),
      ]);

      setInsights(insightsRes.data);

      const map = {};
      subtypesRes.data.forEach(s => {
        map[s.id] = {
          name: s.subtype_name,
          type: s.type
        };
      });
      setSubtypeMap(map);
    } catch (err) {
      console.error('Error fetching insights or subtypes:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInsights();
  }, []);

  const renderContent = () => {
    if (loading || !insights) {
      return (
        <Box sx={{ textAlign: 'center', padding: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    switch (selectedWidget) {
      case 'subtypes':
        return (
          <Box>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
              Top Work Order Subtypes
            </Typography>
            <Stack spacing={1}>
              {insights.topSubtypes.map((item, idx) => {
                const subtype = subtypeMap[item.workorder_subtype_id];
                const colors = ['#1976d2', '#2e7d32', '#d32f2f', '#f9a825', '#6a1b9a'];
                const badgeColor = colors[idx % colors.length];

                return (
                  <Box key={idx}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          backgroundColor: badgeColor,
                          mr: 1,
                          flexShrink: 0
                        }}
                      />
                      <Box>
                        <Typography variant="body1" fontWeight="medium">
                          {subtype?.name || 'Unknown'}
                          <Typography
                            component="span"
                            variant="caption"
                            sx={{ color: 'text.secondary', ml: 1 }}
                          >
                            ({subtype?.type || 'Type N/A'})
                          </Typography>
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          Work Orders: {item.count}
                        </Typography>
                      </Box>
                    </Box>
                    {idx < insights.topSubtypes.length - 1 && <Divider sx={{ my: 1 }} />}
                  </Box>
                );
              })}
            </Stack>
          </Box>
        );

        case 'weekly':
          if (!insights.weeklyByType?.length) {
            return <Typography variant="body2">No type data available for this month.</Typography>;
          }
        
          const colors = {
            Plumbing: '#4caf50',
            Electrical: '#2196f3',
            HVAC: '#f44336',
            Maintenance: '#9c27b0',
            Landscaping: '#ff9800',
          };
        
          const types = Object.keys(insights.weeklyByType[0]).filter(k => k !== 'week');
        
          return (
            <Box>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                Work Order Volume by Type
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 2 }}>
                Grouped by week (this month)
              </Typography>
        
              <ResponsiveContainer width="100%" height={250}>
                <BarChart
                  data={insights.weeklyByType.map(d => ({ ...d, label: `Week ${d.week}` }))}
                  margin={{ top: 10, right: 20, left: 0, bottom: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="label" />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  {types.map((type, idx) => (
                    <Bar
                      key={type}
                      dataKey={type}
                      name={type}
                      fill={colors[type] || `hsl(${(idx * 60) % 360}, 70%, 50%)`}
                      radius={[4, 4, 0, 0]}
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </Box>
          );
        
        

        case 'resolution':
          const { thisMonth, lastMonth } = insights.averageResolutionTime;
  
          const resolutionCards = [
            {
              label: 'Avg. Resolution Time – This Month',
              value: thisMonth != null ? `${thisMonth.toFixed(1)} days` : 'No data',
              color: '#1976d2',
              bg: 'rgba(25, 118, 210, 0.08)',
            },
            {
              label: 'Avg. Resolution Time – Last Month',
              value: lastMonth != null ? `${lastMonth.toFixed(1)} days` : 'No data',
              color: '#2e7d32',
              bg: 'rgba(46, 125, 50, 0.08)',
            },
          ];
  
          const trendMessage = thisMonth != null && lastMonth != null && (
            <Typography variant="caption" sx={{ color: 'text.secondary', mt: 1 }}>
              {thisMonth > lastMonth
                ? '⚠️ Resolution time has increased.'
                : thisMonth < lastMonth
                ? '✅ Improved resolution time!'
                : '↔️ Same as last month.'}
            </Typography>
          );
  
          return (
            <Box>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
                Average Resolution Time
              </Typography>
  
              <Stack spacing={2}>
                {resolutionCards.map((card, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      borderRadius: 2,
                      p: 2,
                      backgroundColor: card.bg,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      boxShadow: 1,
                    }}
                  >
                    <Typography variant="caption" sx={{ fontWeight: 600, color: card.color }}>
                      {card.label}
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', color: card.color }}>
                      {card.value}
                    </Typography>
                  </Box>
                ))}
              </Stack>
  
              {trendMessage}
            </Box>
          );
  

      default:
        return null;
    }
  };



  return (
    <Box sx={{ mt: 5, px: { xs: 2, sm: 3 }, py: 3 }}>
      <Grid container spacing={3}>
        {/* Insights Widget Panel */}
        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ borderRadius: 3, boxShadow: 3, height: '100%' }}>
            <CardContent>
              <Box sx={{ textAlign: 'center', mb: 2 }}>
                <Insights sx={{ fontSize: 32, color: 'primary.main' }} />
                <Typography variant="h6" fontWeight="bold">
                  Work Order Insights
                </Typography>
              </Box>

              <ButtonGroup variant="outlined" fullWidth sx={{ mb: 2 }}>
                <Button
                  onClick={() => setSelectedWidget('subtypes')}
                  variant={selectedWidget === 'subtypes' ? 'contained' : 'outlined'}
                >
                  Subtypes
                </Button>
                <Button
                  onClick={() => setSelectedWidget('weekly')}
                  variant={selectedWidget === 'weekly' ? 'contained' : 'outlined'}
                >
                  Weekly
                </Button>
                <Button
                  onClick={() => setSelectedWidget('resolution')}
                  variant={selectedWidget === 'resolution' ? 'contained' : 'outlined'}
                >
                  Resolution
                </Button>
              </ButtonGroup>

              {renderContent()}
            </CardContent>
          </Card>
        </Grid>

        {/* Calendar Panel */}
        <Grid item xs={12} sm={6} md={8}>
          <Card sx={{ borderRadius: 3, boxShadow: 3, height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Schedule sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" fontWeight="bold">
                  Calendar
                </Typography>
              </Box>
              <Calendar defaultView="month" />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default InsightsWithCalendar;
